import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ImportButton from '@/components/ui-v2/import-button';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DealContext } from '@/lib/contexts/deal.context';
import { GenerationSourceCreatedContext } from '@/lib/contexts/generate-sale-export.context';
import { useDeal } from '@/lib/hooks/useDeal';
import { Provider, createStore } from 'jotai';

type Props = {
  dealId: string;
  onClose: () => void;
  onSourceCreated: (id: string) => void;
  onCloseAutoFocus?: (event: Event) => void;
};

// Import hooks use deal atoms; keep them separate from the home and any offer editor.
export function DealGenerationDialog(props: Props) {
  const [store] = useState(createStore);
  return (
    <Provider store={store}>
      <GenerationSources {...props} />
    </Provider>
  );
}

function GenerationSources({
  dealId,
  onClose,
  onSourceCreated,
  onCloseAutoFocus,
}: Props) {
  const { t } = useTranslation();
  const { deal, initialLoading, error, refetch } = useDeal(dealId, {
    trackView: false,
  });
  const loading = initialLoading && !error;

  // A successful source can change the deal state before its close callback.
  // Keep the wizard mounted until that callback (notably while CSV is uploading).
  if (deal) {
    return (
      <DealContext.Provider value={{ deal, refetchDeal: refetch }}>
        <GenerationSourceCreatedContext.Provider value={onSourceCreated}>
          <ImportButton
            generateSaleExport
            defaultOpen
            onClose={onClose}
            onCloseAutoFocus={onCloseAutoFocus}
          >
            {null}
          </ImportButton>
        </GenerationSourceCreatedContext.Provider>
      </DealContext.Provider>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>{t('deals-list.actions.generate')}</DialogTitle>
        </DialogHeader>
        <p role={loading ? 'status' : 'alert'}>
          {t(loading ? 'deals-list.loading' : 'deals-list.error')}
        </p>
        {!loading && (
          <Button
            variant="outline"
            onClick={() => {
              void refetch().catch(() => {});
            }}
          >
            {t('deals-list.retry')}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
